import { Component, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { OrderListService } from '@app/modules/orders/services';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { ProductionOrderListQuery } from '@app/core/global-state';
import { map, tap } from 'rxjs/operators';
import {
  MachineScheduleViewModel,
  ProductionModeManual,
  ProductionOrderStatus,
  ShiftFilter,
  ProductionMode
} from 'chronos-core-client';
import { ShiftFilterType } from '@app/shared/models';
import { OrderListView } from './order-list-view';

@Component({
  selector: 'app-order-list',
  templateUrl: './order-list.component.html',
  styleUrls: ['./order-list.component.scss']
})
export class OrderListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('loadMoreButton', { read: ElementRef }) loadMoreButton: ElementRef;

  private readonly SEARCH_FILTER_CHAR_LENGTH = 3;

  public selectedShift = ShiftFilterType.All;
  public isShowingAll = false;
  public isExpandCollapseAll = false;
  public automaticPeriodSelectionOnProductionOrderStart = false;
  public isLoading$ = this.productionOrderListQuery.isLoading$;
  public hasData$ = this.productionOrderListQuery.hasData$;

  private orderListFilterSubject = new BehaviorSubject<string>('');
  private orderListFilter$: Observable<string> = this.orderListFilterSubject.asObservable();

  public orderList$: Observable<OrderListView> = combineLatest([this.orderListFilter$, this.productionOrderListQuery.selectAll()]).pipe(
    map(([filter, orderList]) => ({
      filteredList: this.filterOrderList(orderList, filter),
      hasActiveOrder: orderList.some((order) => order.status === ProductionOrderStatus.Active)
    }))
  );
  public shiftFilters$: Observable<ShiftFilter[]>;

  private subscriptions = new Subscription();
  public productionModeManual: ProductionModeManual;
  private intersectionObserver: IntersectionObserver;

  constructor(
    private orderListService: OrderListService,
    private productionOrderListQuery: ProductionOrderListQuery) {}

  public ngOnInit(): void {
    this.showOrders(false);
    this.shiftFilters$ = this.orderListService.getShiftFilters();

    this.subscriptions.add(
      this.orderListService
        .getProductionMode()
        .pipe(
          tap((mode) => {
            this.checkManualMode(mode);
          })
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.orderListService
        .automaticPeriodSelectionOnProductionOrderStart()
        .pipe(
          tap((setting) => {
            this.setAutomaticPeriodSelectionOnProductionOrderStartSetting(setting);
          })
        )
        .subscribe()
    );
  }

  public ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  public showOrders(showAllOrders: boolean): void {
    this.subscriptions.add(this.orderListService.getProductionOrderListByShift(showAllOrders, this.selectedShift).subscribe());
  }

  public trackByFunction(index: number, productionOrder: MachineScheduleViewModel): number {
    return productionOrder.productionOrderId;
  }

  public searchFilterOrderList(filterValue: string): void {
    this.orderListFilterSubject.next(filterValue);
  }

  public filterShift(): void {
    this.showOrders(this.isShowingAll);
  }

  public loadMore(): void {
    this.subscriptions.add(this.orderListService.getNextProductionOrderListByShiftPage().subscribe());
  }

  public hasMoreOrders(): Observable<boolean> {
    return this.hasData$;
  }

  private checkManualMode(mode: ProductionMode): void {
    if (mode === ProductionMode.Manual) {
      this.subscriptions.add(
        this.orderListService
          .getProductionModeManual()
          .pipe(
            tap((productionModeManual) => {
              this.productionModeManual = productionModeManual;
            })
          )
          .subscribe()
      );
    }
  }

  private filterOrderList(orderList: MachineScheduleViewModel[], filter: string): MachineScheduleViewModel[] {
    if (filter.length >= this.SEARCH_FILTER_CHAR_LENGTH) {
      return orderList.filter((order) => order.externalProductionOrderId.toLowerCase().includes(filter.toLowerCase()));
    } else {
      return orderList;
    }
  }

  private setAutomaticPeriodSelectionOnProductionOrderStartSetting(setting: boolean): void {
    this.automaticPeriodSelectionOnProductionOrderStart = setting;
  }

  private setupIntersectionObserver(): void {
    if (!this.loadMoreButton) {
      return;
    }

    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1 // trigger when 10% of the button is visible
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !(entry.target as HTMLButtonElement).disabled) {
          this.loadMore();
        }
      });
    }, options);

    this.intersectionObserver.observe(this.loadMoreButton.nativeElement);
  }
}
